import React, { useState, useEffect } from "react";

import Card from "components/card/Card";
import { IProduct } from "redux/reducers/models/product";
import SelectNew from "components/select/SelectNew";

import { GetProduct, TExtendedProductFilters } from "api/rpc/2024-04/facilityAdmin/product/product";
import axios, { CancelToken } from "axios";
import DataTable from "components/dataTable/DataTable";
import { ButtonNew } from "components/buttonNew";

import { useTranslation } from "react-i18next";

interface IDiscountProductSelectProps {
  stateCallback: (id: string, products: IProduct[]) => void;
  onApplicationSelect: <T extends Record<string, any>>(updated: T, id: string) => void;
  products: IProduct[];
}

export default function DiscountProductSelect(props: IDiscountProductSelectProps) {
  const { t } = useTranslation();
  const { stateCallback, onApplicationSelect, products } = props;

  const [productSearchState, setProductSearchState] = useState<IProduct[]>([]);
  const [filterSearchState, setFilterSearchState] = useState<string>("");

  const optionSelect = [
    {
      id: "included",
      label: t("Included"),
    },
    {
      id: "excluded",
      label: t("Excluded"),
    },
  ];

  // Used for searching all products
  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (() => {
          try {
            if (filterSearchState === "") {
              if (mounted) {
                setProductSearchState([]);
              }
              return;
            } else {
              if (mounted) {
                void productSearch(source.token);
              }
            }
          } catch (error) {
            console.log("err", error);
          }
          return;
        })();
      }, 500);
    };
    search();
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      source.cancel();
    };
  }, [filterSearchState]);

  async function productSearch(token: CancelToken) {
    const builtFilter: { label: keyof TExtendedProductFilters; value: number[] }[] = [];

    // Loop through each department and group by type
    const grouped: Record<string, number[]> = {};

    products.forEach(dep => {
      const key = `${dep.type}_id`; // e.g., department_id, category_id
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(dep.id); // assume id exists
    });

    // Push each group into builtFilter
    for (const [label, value] of Object.entries(grouped)) {
      builtFilter.push({ label: label as keyof TExtendedProductFilters, value });
    }

    const productSearchRes = await GetProduct(
      {
        filters: builtFilter,
        search: filterSearchState,
      },
      false,
      token,
    );

    setProductSearchState(productSearchRes.data);
  }

  function updateApplication<T extends Record<string, any>>(
    selectedApplication: { id: string; label: string },
    item: T,
    id: string,
    productIndex: number,
  ): void {
    const productBuffer = {
      ...products[productIndex],
      pivot: {
        ...products[productIndex].pivot,
        application: selectedApplication.id,
      },
    };

    products[productIndex] = productBuffer;
    onApplicationSelect(products, id);
  }

  function handleSelectProduct(newSelectedProduct: IProduct) {
    let updated;

    if (products.some(product => product.id === newSelectedProduct.id)) {
      updated = products.filter(product => product.id !== newSelectedProduct.id);
    } else {
      const updatedSelected = {
        ...newSelectedProduct,
        pivot: {
          ...newSelectedProduct.pivot,
          application: "included",
        },
      };
      updated = [...products, updatedSelected];
    }

    stateCallback("products", updated);

    if (filterSearchState !== "") {
      setFilterSearchState("");
    }
  }

  return (
    <>
      <Card>
        <Card.Section>
          {/* Product Exclusion Selection */}
          <SelectNew
            label={t("Product Search")}
            id="product_search"
            options={
              productSearchState?.map(product => ({
                id: product.id,
                label: `${product.title}`,
                extraValue: product,
              })) ?? []
            }
            multiValue={products?.map(product => {
              return product.id;
            })}
            onChange={(_, product) => handleSelectProduct(product)}
            searchable
            onSearchCallback={search => setFilterSearchState(search)}
          />

          {/* Excluded Products */}
          <DataTable
            title={t("Products")}
            columns={[
              { label: t("Title") },
              { label: t("Type") },
              { label: t("Application") },
              { label: "", width: "10%" },
            ]}
            overflow
          >
            {products?.map((product, productIndex) => (
              <tr key={product.id}>
                <td>
                  {product.preferred_title ? product.preferred_title : product.title}
                  {product.title !== product.subtitle && <p className="text-sm text-subdued"> {product.subtitle}</p>}
                </td>
                <td>{product.type ?? ""}</td>
                <td>
                  <SelectNew
                    id="application"
                    options={
                      optionSelect?.map(application => ({
                        id: application.id,
                        label: `${application.label}`,
                        extraValue: application,
                      })) ?? []
                    }
                    onChange={(_, extraValue) => updateApplication(extraValue, product, "products", productIndex)}
                    value={product?.pivot?.application}
                  />
                </td>
                <td>
                  <ButtonNew type="text" onClick={() => handleSelectProduct(product)}>
                    {t("Remove")}
                  </ButtonNew>
                </td>
              </tr>
            ))}
          </DataTable>
        </Card.Section>
      </Card>
    </>
  );
}
